import { useId, useState } from "react";
import { Link, useLocation } from "wouter";
import { useAdoptPreview, useAdoptService, useProjects } from "../../api/queries";
import { isConflict } from "../../api/errors";
import type { Container } from "../../api/types";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import { CapturedConfigurationSection } from "../services/CapturedConfigurationSection";
import "../../components/Form.css";
import "./adopt.css";

interface AdoptContainerModalProps {
  /** `null` closes the modal — mirrors `DeletePlacementDialog`'s "target or null" prop
   * shape, convenient for a list-row trigger that doesn't want separate open/target
   * state. */
  container: Container | null;
  onClose: () => void;
}

type Step = "project" | "review";

/**
 * The Migrator "adopt existing container" flow (ADR-0038 P4b, issue #331): choose the
 * target project, then a read-only PREVIEW of the real captured spec — command, entrypoint,
 * and healthcheck MASKED by default (they may embed secrets, ADR-0038 §4) — then confirm.
 *
 * The preview is fetched by `POST .../services/adopt/preview` when the operator advances to
 * the review step; it inspects and returns what WOULD be adopted WITHOUT persisting
 * anything (no service, no placement). The confirm click is what calls
 * `POST .../services/adopt`, which persists. So the Migrator sees the actual (masked)
 * secret-shaped command BEFORE consenting — stronger consent than the old "review what
 * will be read, then adopt, then look at it afterward" flow.
 */
export function AdoptContainerModal({ container, onClose }: AdoptContainerModalProps) {
  const projectFieldId = useId();
  const { pushToast } = useToast();
  const [, navigate] = useLocation();
  const [step, setStep] = useState<Step>("project");
  const [projectId, setProjectId] = useState("");

  const { data: projectsPage, isPending: projectsPending } = useProjects({ limit: 100 });
  const previewMutation = useAdoptPreview(projectId);
  const mutation = useAdoptService(projectId);

  function handleClose() {
    setStep("project");
    setProjectId("");
    previewMutation.reset();
    mutation.reset();
    onClose();
  }

  function handleContinue() {
    if (!container || !projectId) return;
    // Fetch the read-only preview as we enter the review step — persists nothing.
    previewMutation.mutate({ nodeId: container.nodeId, containerId: container.id });
    setStep("review");
  }

  function handleConfirm() {
    if (!container || !projectId) return;
    mutation.mutate(
      { nodeId: container.nodeId, containerId: container.id },
      {
        onSuccess: (service) => {
          pushToast({
            title: `"${container.name}" adopted as service "${service.name}".`,
            description: "Review the captured command/entrypoint/healthcheck on the service page before redeploying.",
            tone: "success",
          });
          handleClose();
          // The service page reads the durable adopted signal off its placements (issue
          // #330) — no sessionStorage hand-off needed.
          navigate(`/projects/${projectId}/services/${service.id}`);
        },
      },
    );
  }

  const projects = projectsPage?.items ?? [];
  const canContinue = projectId.length > 0;
  const preview = previewMutation.data;

  return (
    <Modal open={container !== null} title="Adopt container" onClose={handleClose} initialFocusId={projectFieldId} size="lg">
      {container && step === "project" && (
        <div className="adopt-modal__step">
          <p>
            Adopt <strong>{container.name}</strong> (<span className="adopt-modal__mono">{container.image}</span>) on{" "}
            <strong>{container.nodeName}</strong> into a project as a new, manageable service. Comebye never restarts
            or modifies the container to do this.
          </p>

          <label className="form-field" htmlFor={projectFieldId}>
            <span>Project</span>
            <select
              id={projectFieldId}
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={projectsPending}
              required
            >
              <option value="" disabled>
                {projectsPending ? "Loading projects…" : "Select a project"}
              </option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {!projectsPending && projects.length === 0 && (
              <span className="form-field__hint">
                No projects yet.{" "}
                <Link href="/projects" onClick={handleClose}>
                  Create one
                </Link>{" "}
                first, then come back to adopt this container.
              </span>
            )}
          </label>

          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="button" variant="primary" disabled={!canContinue} onClick={handleContinue}>
              Continue
            </Button>
          </div>
        </div>
      )}

      {container && step === "review" && (
        <div className="adopt-modal__step">
          {mutation.isError && mutation.error && (() => {
            const err = mutation.error;
            const message = err.message || "Couldn't adopt this container.";
            // isConflict (409, e.g. "already adopted" — #324) isn't the operator's
            // mistake, same posture as ServiceDetailPage's deploy-409 handling — render
            // it as an informational note rather than the alarming "couldn't adopt"
            // failure text, but either way the server's own message, never swallowed.
            return isConflict(err) ? (
              <Banner tone="warning" title="Already adopted" className="adopt-modal__banner">
                {message}
              </Banner>
            ) : (
              <p className="form-error" role="alert">
                {message}
              </p>
            );
          })()}

          <dl className="adopt-modal__meta">
            <div>
              <dt>Container</dt>
              <dd>{container.name}</dd>
            </div>
            <div>
              <dt>Image</dt>
              <dd className="adopt-modal__mono">{container.image}</dd>
            </div>
            <div>
              <dt>Node</dt>
              <dd>{container.nodeName}</dd>
            </div>
          </dl>

          <Banner tone="warning" title="This is what would be adopted" className="adopt-modal__banner">
            The configuration below was read directly from Docker — the container itself is never touched (no restart,
            no recreate, no relabel), and nothing is stored until you confirm.{" "}
            <strong>Command, entrypoint, and healthcheck are captured verbatim</strong> and commonly hold secrets (e.g.
            a <code>--password</code> flag or a bearer token in a healthcheck probe), so each is masked below — reveal
            one at a time. Environment variable <strong>names</strong> are captured; values are never read from the
            container and you'll supply them afterward.
          </Banner>

          {previewMutation.isPending && <p className="adopt-modal__loading">Reading the container's configuration…</p>}

          {previewMutation.isError && previewMutation.error && (
            <p className="form-error" role="alert">
              {previewMutation.error.message || "Couldn't read this container's configuration."}
            </p>
          )}

          {preview && <CapturedConfigurationSection service={preview} />}

          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={() => setStep("project")} disabled={mutation.isPending}>
              Back
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={mutation.isPending || previewMutation.isPending}
              onClick={handleConfirm}
            >
              {mutation.isPending ? "Adopting…" : "Adopt container"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
